// src/components/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { formatBlogPublishDate, getBlogPublishDateIso } from '../utils/blogPublishDate';
import SEOHelmet from './SEOHelmet';
import './BlogPost.css';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [blogImage, setBlogImage] = useState(null);

  useEffect(() => {
    // Reset body overflow when component mounts
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    fetchBlogAndContent();
    window.scrollTo(0, 0);
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [id]);

  const fetchBlogAndContent = async () => {
    try {
      // Get blog metadata
      const response = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-blog?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      
      if (response.ok) {
        const blogs = await response.json();
        // Find blog by slug (convert title to slug and match)
        const currentBlog = blogs.find(b => {
          const blogSlug = b.title.toLowerCase()
            .replace(/[^a-z0-9 -]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim('-');
          return blogSlug === id || b.id === parseInt(id); // Support both slug and ID for backwards compatibility
        });
        
        if (currentBlog) {
          setBlog(currentBlog);
          
          // Fetch blog image if available
          if (currentBlog.file_id) {
            try {
              const imageResponse = await fetch(`https://tophamtechbe.com/api/treas/blogs/v1/get-image?site_id=${SITE_CONFIG.SITE_ID}&s3_blog_file_id=${currentBlog.file_id}`, {
                headers: { 'X-API-Key': 'treas-api-key-2025' }
              });
              if (imageResponse.ok) {
                const imageData = await imageResponse.json();
                setBlogImage(imageData.url);
              }
            } catch (error) {
              console.error('Error fetching blog image:', error);
            }
          }
          
          // Fetch actual HTML content from S3 - this renders on your domain!
          try {
            const contentResponse = await fetch(currentBlog.url, {
              mode: 'cors',
              headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
              }
            });
            if (contentResponse.ok) {
              const htmlContent = await contentResponse.text();
              setContent(htmlContent);
            } else {
              // Fallback to iframe if direct fetch fails
              setContent(`<iframe src="${currentBlog.url}" style="width:100%;height:800px;border:none;" title="${currentBlog.title}"></iframe>`);
            }
          } catch (corsError) {
            console.log('CORS error, using iframe fallback:', corsError);
            // Fallback to iframe for CORS issues
            setContent(`<iframe src="${currentBlog.url}" style="width:100%;height:800px;border:none;" title="${currentBlog.title}"></iframe>`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
      // Ensure body is scrollable after content loads
      document.body.style.overflow = 'auto';
      setTimeout(() => window.scrollTo(0, 0), 100);
    }
  };

  if (loading) return <div className="blog-loading">Loading blog post...</div>;
  if (!blog) return <div className="blog-not-found">Blog post not found</div>;

  return (
    <div className="blog-post-page">
      <SEOHelmet 
        title={`${blog.title} | ${SITE_CONFIG.AGENT.NAME} Real Estate Blog`}
        description={blog.meta_description || `${blog.title} — real estate guidance from ${SITE_CONFIG.AGENT.NAME}.`}
        path={`/blog/${id}`}
        schemaType="BlogPosting"
        breadcrumb={blog.title}
      />
      
      <div className="blog-header">
        <button onClick={() => navigate('/blogs')} className="back-to-blogs" aria-label="Return to blog list page">
          ← Back to Blog
        </button>
        <button onClick={() => navigate('/')} className="back-to-home" aria-label="Return to home page">
          Home
        </button>
      </div>
      
      <article className="blog-article" role="article" aria-label={`Blog post: ${blog.title}`}>
        {blogImage && (
          <div className="blog-hero-image">
            <img src={blogImage} alt={`${blog.title} - Featured image for blog post`} />
          </div>
        )}
        
        <header className="blog-article-header">
          <div className="blog-meta">
            <time className="blog-date" dateTime={getBlogPublishDateIso(blog) || undefined}>
              {formatBlogPublishDate(blog)}
            </time>
            <span className="blog-author">By Sandy Hopkins</span>
          </div>
        </header>
        
        <div className="blog-content" dangerouslySetInnerHTML={{ __html: content }} />
      </article>
    </div>
  );
};

export default BlogPost;
